import { useCallback, useEffect, type RefObject } from 'react'

type UseClipboardBridgeOptions = {
  iframeRef: RefObject<HTMLIFrameElement | null>
  containerRef?: RefObject<HTMLElement | null>
  enabled?: boolean
}

export function useClipboardBridge({ iframeRef, containerRef, enabled = true }: UseClipboardBridgeOptions) {
  const handleMessage = useCallback(
    async (event: MessageEvent) => {
      if (!enabled) return
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || event.source !== iframeWindow) {
        return
      }

      const postResponse = (payload: unknown) => {
        iframeWindow.postMessage(payload, '*')
      }

      try {
        switch (event.data?.type) {
          case 'requestClipboardRead': {
            if (!navigator.clipboard?.readText) {
              postResponse({
                type: 'clipboardReadResponse',
                error: 'Clipboard read unsupported',
                requestId: event.data?.requestId,
              })
              return
            }

            try {
              const text = await navigator.clipboard.readText()
              postResponse({
                type: 'clipboardReadResponse',
                text,
                requestId: event.data?.requestId,
              })
            } catch (error) {
              console.error('Failed to read clipboard:', error)
              postResponse({
                type: 'clipboardReadResponse',
                error: 'Failed to read clipboard',
                requestId: event.data?.requestId,
              })
            }
            break
          }
          case 'requestClipboardWrite': {
            if (!navigator.clipboard?.writeText) {
              postResponse({
                type: 'clipboardWriteResponse',
                success: false,
                error: 'Clipboard write unsupported',
                requestId: event.data?.requestId,
              })
              return
            }

            try {
              await navigator.clipboard.writeText(event.data?.text ?? '')
              postResponse({
                type: 'clipboardWriteResponse',
                success: true,
                requestId: event.data?.requestId,
              })
            } catch (error) {
              console.error('Failed to write clipboard:', error)
              postResponse({
                type: 'clipboardWriteResponse',
                success: false,
                error: 'Failed to write to clipboard',
                requestId: event.data?.requestId,
              })
            }
            break
          }
          case 'clipboardBridgeReady': {
            break
          }
        }
      } catch (error) {
        console.error('Error handling clipboard bridge message:', error)
      }
    },
    [enabled, iframeRef],
  )

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      if (!enabled) return

      const container = containerRef?.current
      if (
        container &&
        document.activeElement &&
        container !== document.activeElement &&
        !container.contains(document.activeElement)
      ) {
        return
      }

      const isCtrlOrCmd = event.metaKey || event.ctrlKey
      const iframeWindow = iframeRef.current?.contentWindow
      if (!iframeWindow || !isCtrlOrCmd) return

      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault()
        iframeWindow.postMessage({ type: 'triggerCopy' }, '*')
        return
      }

      if (event.key === 'v' || event.key === 'V') {
        event.preventDefault()
        if (!navigator.clipboard?.readText) return
        try {
          const text = await navigator.clipboard.readText()
          if (text) {
            iframeWindow.postMessage(
              {
                type: 'triggerPaste',
                text,
              },
              '*',
            )
          }
        } catch (error) {
          console.error('Failed to read clipboard for paste:', error)
        }
      }
    },
    [containerRef, enabled, iframeRef],
  )

  useEffect(() => {
    if (!enabled) return
    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [enabled, handleMessage])

  useEffect(() => {
    if (!enabled) return
    document.addEventListener('keydown', handleKeyDown, true)
    return () => document.removeEventListener('keydown', handleKeyDown, true)
  }, [enabled, handleKeyDown])

  useEffect(() => {
    if (!enabled) return
    const container = containerRef?.current
    if (!container) return

    const handleFocusRequest = () => container.focus()
    container.addEventListener('click', handleFocusRequest)
    return () => container.removeEventListener('click', handleFocusRequest)
  }, [containerRef, enabled])
}

export function ensureClipboardBridgeParam(url: string | null | undefined) {
  if (!url) return null
  if (/(?:\?|&)clipboardBridge=/.test(url)) {
    return url
  }
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}clipboardBridge=true`
}
